import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerHeader,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { useIsUncouplePatientSessionDeletionEnabled } from '@/features/flags';
import { useMutateDeletePatientProfile } from '@/hooks/mutation/useMutateDeletePatientProfile';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { logger } from '@/utils/logger/logger';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import { DialogDescription } from '@heidi/design-system/dialog';
import { Label } from '@heidi/design-system/label';
import { TypographyH5, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useQueryLinkedSessionsByProfileId } from '@heidi/session/data';
import { DialogTitle } from '@radix-ui/react-dialog';
import { atom, useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useIntl } from 'react-intl';

export const UnLinkPatientAllSessionsModalOpenAtom = atom<boolean>(false);
export const DeletePatientProfileIdAtom = atom<string | undefined>(undefined);

const UnLinkPatientWithAllSessionsModal = () => {
  const intl = useIntl();
  const [open, setOpen] = useAtom(UnLinkPatientAllSessionsModalOpenAtom);
  const [deletePatientProfileId] = useAtom(DeletePatientProfileIdAtom);
  const [alsoDeleteSessions, setAlsoDeleteSessions] = useState(false);
  const { mutateAsync: deletePatientProfile, isPending } =
    useMutateDeletePatientProfile();
  const { data: patientProfiles } = useQueryPatientProfiles();
  const { t } = useTranslations();
  const isUncoupleEnabled = useIsUncouplePatientSessionDeletionEnabled();
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  // Team-reviewer deletes route through the team-member mutation which always
  // cascades server-side; hiding the checkbox in that mode prevents silent
  // data loss where an unchecked "Also delete sessions" would still cascade.
  // Mirrors the guard in delete-patient-confirm.tsx.
  const isTeamReviewerMode = isReviewingOtherUser;
  const patientProfile = Array.isArray(patientProfiles)
    ? patientProfiles.find((profile) => profile.id === deletePatientProfileId)
    : undefined;

  const { data: linkedSessions } = useQueryLinkedSessionsByProfileId(
    patientProfile?.id || ''
  );
  const sessionId = useCurrentSessionId();
  const router = useRouter();
  const linkedSessionsCount = linkedSessions?.sessions?.length || 0;
  const hasLinkedSessions = linkedSessionsCount > 0;
  // The checkbox only appears when the uncouple flag is on, the patient has
  // sessions to make a choice about, AND the active mutation honours the
  // choice (i.e. not the team-member endpoint which always cascades).
  // Outside that, the modal stays on the legacy single-button cascade path.
  const showCascadeCheckbox =
    isUncoupleEnabled && hasLinkedSessions && !isTeamReviewerMode;
  const willCascade = showCascadeCheckbox ? alsoDeleteSessions : true;

  // Reset the cascade opt-in synchronously inside the open-change handler
  // so successive open/close cycles can't carry a stale `true` through a
  // useEffect commit. Mirrors the pattern in the new modal.
  const handleOpenChange = (next: boolean) => {
    if (!next) setAlsoDeleteSessions(false);
    setOpen(next);
  };

  const unlinkModalTitleKey =
    linkedSessionsCount === 1
      ? 'patient.unlinkModalTitle_one'
      : 'patient.unlinkModalTitle_other';
  const onConfirmClick = async () => {
    if (!patientProfile) return;
    try {
      await deletePatientProfile({
        patientProfile,
        deleteLinkedSessions: willCascade,
      });
      // The patient was deleted. If the cascade ran AND the current
      // session was one of the deleted ones, bounce off it.
      if (
        willCascade &&
        linkedSessions?.sessions?.some((session) => session.session_id === sessionId)
      ) {
        void router.push(`/scribe`);
      }
      // Close only on success. A thrown delete must leave the modal open so
      // the user can retry — mirrors the new modal's error behaviour. The
      // mutation surfaces its own error toast via the shared onError.
      handleOpenChange(false);
    } catch (error) {
      logger.error('Error:', error);
    }
  };

  const renderBody = () => {
    if (!showCascadeCheckbox) {
      // Flag off or no linked sessions: keep the legacy translation-driven body.
      return (
        <TypographyP2 className="text-text-secondary">
          {t('patient.unlinkModalBody')}
        </TypographyP2>
      );
    }
    const irreversibility = intl.formatMessage({
      defaultMessage: 'This action cannot be undone.',
      id: 'JDJoIZ0VZp',
    });
    const sessionFate = willCascade
      ? intl.formatMessage(
          {
            defaultMessage:
              '{sessionCount, plural, one {The linked session will be deleted along with the patient.} other {All # linked sessions will be deleted along with the patient.}}',
            id: 'y9xclOcHkI',
          },
          { sessionCount: linkedSessionsCount }
        )
      : intl.formatMessage(
          {
            defaultMessage:
              '{sessionCount, plural, one {The linked session will be retained but become unlinked.} other {The # current linked sessions will be retained but become unlinked.}}',
            id: 'ot6NnUgM1T',
          },
          { sessionCount: linkedSessionsCount }
        );
    // Two stacked spans (display: block) inside one TypographyP2 instead of
    // <br/><br/> so the paragraph break reads as a gap, not as two empty
    // inline breaks. Avoids the screen-reader-hostile br/br smell.
    return (
      <TypographyP2 className="text-text-secondary">
        <span className="block">{irreversibility}</span>
        <span className="mt-2 block">{sessionFate}</span>
      </TypographyP2>
    );
  };

  const renderTitle = () => {
    if (showCascadeCheckbox) {
      // Flag on with sessions: align the title with the new modal so the
      // legacy "Unlink patient with N sessions?" copy doesn't contradict the
      // checkbox-driven choice we're now offering.
      return intl.formatMessage({
        defaultMessage: 'Delete this patient?',
        id: 'vvyINbiaPM',
      });
    }
    return t(unlinkModalTitleKey, { count: linkedSessionsCount });
  };

  return (
    <DialogDrawer open={open} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className="w-full gap-4 p-6 sm:max-w-[500px]"
        hideCloseIcon
        dismissible={false}
      >
        <div className="flex w-full flex-col gap-2">
          <DialogDrawerHeader>
            <DialogTitle>
              <TypographyH5>{renderTitle()}</TypographyH5>
            </DialogTitle>
          </DialogDrawerHeader>
          <DialogDescription>{renderBody()}</DialogDescription>
        </div>

        {/* Footer row: checkbox left, buttons right on desktop; stacks
            (col-reverse) on mobile so Delete sits at the top, Cancel below,
            checkbox at the bottom. Mirrors the new modal's footer pattern
            for uniformity across the two surfaces. */}
        <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:items-center sm:justify-end">
          {showCascadeCheckbox ? (
            <div className="flex items-center gap-2 sm:me-auto">
              <Checkbox
                id="unlink-modal-also-delete-sessions"
                checked={alsoDeleteSessions}
                onCheckedChange={(value) => setAlsoDeleteSessions(value === true)}
                disabled={isPending}
              />
              <Label
                htmlFor="unlink-modal-also-delete-sessions"
                className="text-text-primary"
              >
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'Also delete {sessionCount, plural, one {the linked session} other {all # linked sessions}}',
                    id: 'M0XnTz6GT2',
                  },
                  { sessionCount: linkedSessionsCount }
                )}
              </Label>
            </div>
          ) : null}
          {/* Buttons branch on the uncouple checkbox state:
              - Flag OFF (legacy): keep the existing `size="lg"` + legacy
                cascade-implying copy so production users see exactly what
                they see today.
              - Flag ON (checkbox visible): match the new modal's default
                button size and use neutral "Delete patient" wording —
                cascade is implied by the checkbox now, not the button. */}
          {showCascadeCheckbox ? (
            <>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => {
                  handleOpenChange(false);
                }}
              >
                {intl.formatMessage({ defaultMessage: 'Cancel', id: '47FYwba+bI' })}
              </Button>
              <Button
                size="lg"
                variant="destructive"
                onClick={() => void onConfirmClick()}
              >
                {intl.formatMessage({
                  defaultMessage: 'Delete patient',
                  id: '7uSfsL0+80',
                })}
              </Button>
            </>
          ) : (
            <>
              <Button
                disableAutomaticSvgSizing
                variant="ghost"
                onClick={() => {
                  handleOpenChange(false);
                }}
                size={'lg'}
              >
                {t('patient.cancel')}
              </Button>
              <Button
                disableAutomaticSvgSizing
                variant={'destructive'}
                size={'lg'}
                onClick={() => void onConfirmClick()}
              >
                {t('patient.deletePatientProfile')}
              </Button>
            </>
          )}
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

export default UnLinkPatientWithAllSessionsModal;
